import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { createAudit } from '@/services/audit';
import {
	getUserPresenceSessionId,
	updateUserPresence,
} from '@/services/users';
import type { CreateAuditDto } from '@/types/audit';
import type { AuthState, UserWithAuthData } from '@/types/auth';
import { useClinicStore } from './clinicStore';
import { useDateFilterStore } from './dateFilterStore';
import { useErrorStore } from './errorStore';
import { useInvoiceCartStore } from './invoiceCartStore';
import { useQuickActionsStore } from './quickActionsStore';
import { useReleaseStore } from './releaseStore';

export interface AuthInitialState {
	user: UserWithAuthData | null;
	accessToken: string | null;
	refreshToken: string | null;
	pageConfig: any | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	isLoggingOut: boolean; // Track if logout is in progress
}
// Initial state
const initialState: AuthInitialState = {
	user: null,
	accessToken: null,
	refreshToken: null,
	pageConfig: null,
	isAuthenticated: false,
	isLoading: false,
	isLoggingOut: false,
};

/**
 * Auth Store - Centralized authentication state management
 *
 * Features:
 * - Persists to localStorage automatically
 * - Includes devtools for debugging
 * - Type-safe with TypeScript
 * - Centralized token management
 */
export const useAuthStore = create<AuthState>()(
	devtools(
		persist(
			(set, get) => ({
				...initialState,

				/**
				 * Set complete auth state (user + tokens + pageConfig)
				 * Used after successful login
				 */
				setAuth: (user, tokens, pageConfig = null) => {
					set(
						{
							user,
							accessToken: tokens.access_token,
							refreshToken: tokens.refresh_token || null,
							pageConfig: pageConfig || null,
							isAuthenticated: !!tokens.access_token,
							isLoading: false,
						},
						false,
						'auth/setAuth',
					);
				},

				/**
				 * Update user information (replaces entire user object)
				 */
				setUser: (user) => {
					set(
						{
							user,
							isAuthenticated: !!user,
						},
						false,
						'auth/setUser',
					);
				},

				/**
				 * Update only user.userInformation (partial update)
				 */
				updateUserInformation: (userData) => {
					const currentUser = get().user;
					if (currentUser?.userInformation) {
						set(
							{
								user: {
									...currentUser,
									userInformation: {
										...currentUser.userInformation,
										...userData,
									},
								},
							},
							false,
							'auth/updateUserInformation',
						);
					}
				},

				/**
				 * Update user preferences (locationPreferences, salesRepPreferences, clinicPreferences)
				 */
				updateUserPreferences: (preferences) => {
					const currentUser = get().user;
					if (currentUser?.userInformation) {
						set(
							{
								user: {
									...currentUser,
									userInformation: {
										...currentUser.userInformation,
										...preferences,
									},
								},
							},
							false,
							'auth/updateUserPreferences',
						);
					}
				},

				updateMsProducts: (msProducts) => {
					const currentUser = get().user;
					if (currentUser?.userInformation) {
						set(
							{
								user: {
									...currentUser,
									userInformation: {
										...currentUser.userInformation,
										commissionableProducts: msProducts,
									},
								},
							},
							false,
							'auth/updateMsProducts',
						);
					}
				},

				/**
				 * Update tokens (both access and refresh)
				 */
				setTokens: (tokens) => {
					set(
						{
							accessToken: tokens.access_token,
							refreshToken: tokens.refresh_token || get().refreshToken,
							isAuthenticated: !!tokens.access_token,
						},
						false,
						'auth/setTokens',
					);
				},

				/**
				 * Update only access token (useful for token refresh)
				 */
				updateAccessToken: (token) => {
					set(
						{
							accessToken: token,
							isAuthenticated: !!token,
						},
						false,
						'auth/updateAccessToken',
					);
				},

				/**
				 * Set page configuration
				 */
				setPageConfig: (pageConfig) => {
					set(
						{
							pageConfig,
						},
						false,
						'auth/setPageConfig',
					);
				},

			/**
			 * Logout - Clear all auth state, storage, and Zustand stores
			 */
			logout: async () => {
				// Prevent multiple logout calls
				if (get().isLoggingOut) {
					return;
				}

				// Set logout in progress
				set({ isLoggingOut: true }, false, 'auth/loggingOut');

				// Send logout audit log before clearing data
				try {
					const auditUserStr = localStorage.getItem('auditUser');
					if (auditUserStr) {
						const auditUser = JSON.parse(auditUserStr);

						// Create logout audit data with only valid DTO fields
						const auditData: CreateAuditDto = {
							nUserId: auditUser.nUserId,
							userEmail: auditUser.userEmail,
							fullName: auditUser.fullName,
							Port: auditUser.port,
							ipAddress: auditUser.ipAddress,
							state: auditUser.state,
							country: auditUser.country,
							city: auditUser.city,
							userType: auditUser.userType,
							userClinicIds: auditUser.userClinicIds,
							userClinicNames: auditUser.userClinicNames,
							originalUrl: '/auth/logout',
							originalMethod: 'POST',
							originalPayload: JSON.stringify({}),
						};

						// Use the audit service (fire and forget)
						await createAudit(auditData);
					}
				} catch (error) {
					// Silent fail - don't block logout if audit fails
				}

				try {
					const userId = get().user?.userInformation?._id;
					const presenceSessionId = getUserPresenceSessionId();

					if (userId && presenceSessionId) {
						await updateUserPresence(userId, {
							sessionId: presenceSessionId,
							isLoggedOut: true,
						});
					}
				} catch (error) {
					// Silent fail - don't block logout if presence sync fails
				}

				// Reset auth store state FIRST before clearing storage
				// This prevents the persist middleware from restoring old state
				set(
					{
						...initialState,
					},
					false,
					'auth/logout',
				);

				// Clear other Zustand stores
				useQuickActionsStore.getState().clearQuickActions();
				useInvoiceCartStore.getState().clearCart();
				useDateFilterStore.getState().reset();
				useClinicStore.getState().resetClinics();
				useErrorStore.getState().clearError();
				
				useReleaseStore.getState().setPollingRelease(false);
				useReleaseStore.getState().setVersion('');

				// Clear all browser storage AFTER resetting state
				localStorage.clear();
				sessionStorage.clear();
			},

				/**
				 * Reset to initial state
				 */
				reset: () => {
					set(initialState, false, 'auth/reset');
				},
			}),
			{
				name: 'auth-storage', // localStorage key
				// Optionally, you can customize what gets persisted
				partialize: (state) => ({
					user: state.user,
					accessToken: state.accessToken,
					refreshToken: state.refreshToken,
					pageConfig: state.pageConfig,
					isAuthenticated: state.isAuthenticated,
					// Don't persist isLoading
				}),
			},
		),
		{
			name: 'AuthStore',
			enabled: import.meta.env.DEV, // Enable devtools only in development
		},
	),
);

// Selectors - for optimized component re-renders
export const selectIsAuthenticated = (state: AuthState) =>
	state.isAuthenticated;
export const selectUser = (state: AuthState) => state.user;
export const selectAccessToken = (state: AuthState) => state.accessToken;
export const selectRefreshToken = (state: AuthState) => state.refreshToken;
export const selectPageConfig = (state: AuthState) => state.pageConfig;
export const selectTutorialRemaining = (state: AuthState) =>
	state.user?.userInformation?.tutorialRemaining;
